import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/axios';
import WasteClassifier from './WasteClassifier';
import WelcomePopup from './WelcomePopup'; // portal-based welcome popup
import {
  FileText,
  Award,
  MapPin,
  Bell,
  Calendar,
  Star,
  AlertCircle,
  Trash2,
  CheckCircle,
  Camera,
  RefreshCw,
  BarChart3,
  QrCode,
  X
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './UserDashboard.css';

const mockChartData = [
  { month: 'Jan', reports: 4 },
  { month: 'Feb', reports: 6 },
  { month: 'Mar', reports: 3 },
  { month: 'Apr', reports: 8 },
  { month: 'May', reports: 5 },
  { month: 'Jun', reports: 7 }
];

const UserDashboard = () => {
  const { user } = useAuth();
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [userReports, setUserReports] = useState([]);
  const [userStats, setUserStats] = useState({
    totalReports: 0,
    pendingReports: 0,
    resolvedReports: 0,
    rewardPoints: 0
  });

  // NEW STATE FOR MODAL
  const [showClassifier, setShowClassifier] = useState(false);

  // show welcome if not seen
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem(`welcome_seen_${user?.id || 'user'}`);
    if (!hasSeenWelcome && user) {
      setShowWelcomePopup(true);
    }
  }, [user]);

  // fetch user reports & stats
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        const reportsResponse = await api.get(`/reports/user/${user.id}`);
        const reports = reportsResponse.data.reports || [];
        setUserReports(reports);

        const stats = {
          totalReports: reports.length,
          pendingReports: reports.filter(r => r.status === 'pending').length,
          resolvedReports: reports.filter(r => r.status === 'resolved').length,
          rewardPoints: reports.length * 10
        };
        setUserStats(stats);
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  // lock body scroll when either modal is open
  useEffect(() => {
    if (showWelcomePopup || showClassifier) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [showWelcomePopup, showClassifier]);

  const handleCloseWelcome = () => {
    setShowWelcomePopup(false);
    localStorage.setItem(`welcome_seen_${user?.id || 'user'}`, 'true');
  };

  const quickStats = [
    { label: 'Reward Points', value: loading ? '...' : userStats.rewardPoints.toString(), icon: <Award />, route: '/rewards' },
    { label: 'Reports Made', value: loading ? '...' : userStats.totalReports.toString(), icon: <FileText />, route: '/reports' },
    { label: 'Pending Reports', value: loading ? '...' : userStats.pendingReports.toString(), icon: <AlertCircle />, route: '/reports' },
    { label: 'Resolved Reports', value: loading ? '...' : userStats.resolvedReports.toString(), icon: <CheckCircle />, route: '/reports' }
  ];

  const getRecentReports = () => {
    return userReports.slice(0, 3).map(report => {
      const timeAgo = new Date(report.createdAt);
      const now = new Date();
      const diffDays = Math.floor((now - timeAgo) / (1000 * 60 * 60 * 24));
      const timeText = diffDays === 0 ? 'Today' :
                       diffDays === 1 ? 'Yesterday' :
                       `${diffDays} days ago`;

      return {
        id: report._id,
        status: report.status,
        message: `${report.issueType.replace('_', ' ')} reported`,
        time: timeText
      };
    });
  };

  const upcomingEvents = [
    {
      title: 'Community Cleanup Drive',
      date: 'Saturday, 10:00 AM',
      location: 'Central Park',
      points: 100
    },
    {
      title: 'Beach Cleanup Drive',
      date: 'Thursday, 7:00 AM',
      location: 'Marina Beach',
      points: 120
    }
  ];

  const notifications = [
    { type: 'success', message: 'Your last report was resolved', time: '1h ago' },
    { type: 'info', message: 'New event in your area', time: '3h ago' },
    { type: 'warning', message: 'Bin #456 needs attention', time: '5h ago' }
  ];

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setLastUpdate(new Date());
      setLoading(false);
    }, 1000);
  };

  return (
    <div className="user-dashboard">
      {/* Welcome Popup (portal-based) */}
      {showWelcomePopup && (
        <WelcomePopup user={user} onClose={handleCloseWelcome} />
      )}

      {/* Classifier Modal (unchanged) */}
      {showClassifier && (
        <div className="classifier-modal-overlay" onClick={() => setShowClassifier(false)}>
          <div className="classifier-modal-content" onClick={(e) => e.stopPropagation()}>
            <WasteClassifier onClose={() => setShowClassifier(false)} />
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="dashboard-card full-width quick-actions-section">
        <div className="card-header">
          <h2>Quick Actions</h2>
          <p className="section-subtitle">Get started with these essential features</p>
        </div>
        <div className="quick-actions-grid">
          <Link to="/scanner" className="quick-action-card primary">
            <QrCode size={24} />
            <h3>Scan QR</h3>
            <p>Scan bin QR codes for quick reporting</p>
          </Link>

          <div
            className="quick-action-card"
            onClick={() => setShowClassifier(true)}
            style={{ cursor: 'pointer' }}
          >
            <Camera size={24} />
            <h3>Quick Identify</h3>
            <p>Take a photo to identify waste type</p>
          </div>

          <Link to="/nearby-bins" className="quick-action-card">
            <MapPin size={24} />
            <h3>Find Nearby Bins</h3>
            <p>Locate bins in your area with GPS</p>
          </Link>

          <Link to="/rewards/redeem" className="quick-action-card">
            <Award size={24} />
            <h3>Redeem Points</h3>
            <p>Use your earned points for rewards</p>
          </Link>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="stats-grid">
        {quickStats.map((stat, index) => (
          <Link to={stat.route} key={index} className="stat-card">
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-details">
              <h3>{stat.value}</h3>
              <p>{stat.label}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* Activity Chart */}
      <div className="dashboard-card full-width activity-chart-section">
        <div className="card-header">
          <h2><BarChart3 size={20} /> Reporting Activity</h2>
        </div>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={mockChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="reports" stroke="#4CAF50" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Recent Reports */}
        <div className="dashboard-card">
          <div className="card-header">
            <h2><FileText size={20} /> Recent Reports</h2>
            <Link to="/reports" className="view-all-link">View All</Link>
          </div>
          <div className="reports-list">
            {getRecentReports().map((report) => (
              <div key={report.id} className="report-item">
                <div className={`status-badge ${report.status}`}>
                  {report.status === 'resolved' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                  {report.status}
                </div>
                <div className="report-content">
                  <p>{report.message}</p>
                  <span>{report.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notifications */}
        <div className="dashboard-card">
          <div className="card-header">
            <h2><Bell size={20} /> Notifications</h2>
          </div>
          <div className="notifications-list">
            {notifications.map((notification, index) => (
              <div key={index} className={`notification-item ${notification.type}`}>
                {notification.type === 'success' && <CheckCircle className="notification-icon" />}
                {notification.type === 'info' && <Bell className="notification-icon" />}
                {notification.type === 'warning' && <AlertCircle className="notification-icon" />}
                <div className="notification-content">
                  <p>{notification.message}</p>
                  <span>{notification.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="dashboard-card upcoming-events-section">
          <div className="card-header">
            <h2><Calendar size={20} /> Upcoming Events</h2>
            <Link to="/events" className="view-all-link">View All</Link>
          </div>
          <div className="events-list scrollable-events">
            {upcomingEvents.map((event, index) => (
              <div key={index} className="event-item">
                <div className="event-icon">
                  <Calendar size={24} />
                </div>
                <div className="event-details">
                  <h3>{event.title}</h3>
                  <p className="event-time">{event.date}</p>
                  <p className="event-location">{event.location}</p>
                </div>
                <div className="event-points">
                  <Star size={16} />
                  <span>{event.points} points</span>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default UserDashboard;
